import type { MerchantOfferAddressDetailVO } from "@/api/merchant.api";
import { parseTermsAndConditionsApiValue } from "@/components/merchant/TermsAndConditionsSection";
import { cn } from "@/lib/utils";
import { getFormattedDateTime } from "@/utils";
import type {
  ProgramDetailsOfferItem,
  PublicOffer,
} from "./publicOffer.types";

/** Card fill — cool blue diagonal */
export const OFFER_CARD_BG =
  "linear-gradient(109.6deg, rgba(223,234,247,1) 11.2%, rgba(244,248,252,1) 91.1%)";

export const PUBLIC_OFFER_CTA_BUTTON_CLASS = cn(
  "group/btn relative mt-5 flex w-full items-center justify-center overflow-hidden !rounded-none py-3 text-sm font-semibold text-white",
  "bg-gradient-to-r from-violet-600 to-blue-500 shadow-md shadow-violet-500/30",
  "transition-all duration-300 ease-out",
  "hover:scale-[1.02] hover:from-violet-500 hover:to-indigo-500 hover:shadow-lg hover:shadow-violet-500/40",
  "active:scale-[0.98]",
  "before:pointer-events-none before:absolute before:inset-0 before:z-0 before:-translate-x-full before:skew-x-12 before:bg-gradient-to-r before:from-transparent before:via-white/25 before:to-transparent before:transition-transform before:duration-700 before:ease-out before:content-['']",
  "hover:before:translate-x-full",
);

export function resolvePublicAssetUrl(
  apiBaseUrl: string,
  uri: string | undefined | null
): string | undefined {
  const u = (uri || "").trim();
  if (!u) return undefined;
  if (u.startsWith("http://") || u.startsWith("https://")) return u;
  if (!apiBaseUrl) return u;
  return `${apiBaseUrl.replace(/\/$/, "")}${u.startsWith("/") ? u : `/${u}`}`;
}

export function formatOfferEndDate(endDateInMillis?: number): string {
  if (endDateInMillis == null || !Number.isFinite(Number(endDateInMillis))) {
    return "—";
  }
  const formatted = getFormattedDateTime(Number(endDateInMillis));
  return formatted ? `Ends ${formatted}` : "—";
}

export function formatOfferStartDate(startDateInMillis?: number): string {
  if (startDateInMillis == null || !Number.isFinite(Number(startDateInMillis))) {
    return "—";
  }
  const formatted = getFormattedDateTime(Number(startDateInMillis));
  return formatted ? `Starts ${formatted}` : "—";
}

export function parseOfferAddressDetailsFromItem(
  raw: ProgramDetailsOfferItem
): MerchantOfferAddressDetailVO[] | undefined {
  const rawList = raw.addressDetailsVOList;
  if (!Array.isArray(rawList) || rawList.length === 0) return undefined;
  const out: MerchantOfferAddressDetailVO[] = [];
  for (const item of rawList) {
    if (!item || typeof item !== "object") continue;
    const row = item as MerchantOfferAddressDetailVO;
    const hasContent =
      row.address ||
      row.city ||
      row.country ||
      row.location ||
      row.phoneNumber;
    if (!hasContent) continue;
    out.push({
      address: row.address ?? "",
      city: row.city ?? "",
      country: row.country ?? "",
      location: row.location ?? "",
      phoneCountryCode: row.phoneCountryCode ?? "",
      phoneNumber: row.phoneNumber ?? "",
      ...(row.id != null && Number.isFinite(Number(row.id))
        ? { id: Number(row.id) }
        : {}),
    });
  }
  return out.length ? out : undefined;
}

export function normalizePublicOfferUrl(
  value: string | null | undefined
): string | undefined {
  if (value == null) return undefined;
  const trimmed = String(value).trim();
  if (!trimmed || trimmed.toLowerCase() === "null") return undefined;
  return trimmed;
}

function parseOfferTermsLines(
  raw: string | string[] | undefined | null
): string[] | undefined {
  if (raw == null) return undefined;
  if (Array.isArray(raw)) {
    const lines = raw.map((line) => String(line).trim()).filter(Boolean);
    return lines.length ? lines : undefined;
  }
  const lines = parseTermsAndConditionsApiValue(raw);
  return lines.length ? lines : undefined;
}

export function mapProgramOfferToPublicOffer(
  raw: ProgramDetailsOfferItem,
  ctx: {
    apiBaseUrl: string;
    programName: string;
    programLogoUrl?: string;
    defaultMerchantImageUrl?: string;
  }
): PublicOffer {
  const desc = (raw.description || "").trim();
  const cat = raw.categoryVO;
  const description = desc || `Offer from ${ctx.programName}.`;

  const detailImageUrl = resolvePublicAssetUrl(ctx.apiBaseUrl, raw.imageUri);
  const merchantImageUrl = resolvePublicAssetUrl(
    ctx.apiBaseUrl,
    raw.merchantOfOfferVO?.merchantImageUri
  );
  const cardImageUrl =
    merchantImageUrl ||
    ctx.defaultMerchantImageUrl ||
    ctx.programLogoUrl ||
    undefined;

  const endMs =
    raw.endDateInMillis != null && Number.isFinite(Number(raw.endDateInMillis))
      ? Number(raw.endDateInMillis)
      : undefined;

  const merchantName =
    (raw.merchantOfOfferVO?.merchantName || "").trim() || ctx.programName;
  const title = (raw.title || "").trim() || ctx.programName;

  return {
    id: String(raw.id),
    cardImageUrl,
    detailImageUrl,
    imageAlt: `${title} — ${merchantName}`,
    title,
    description,
    merchantName,
    categoryName: (cat?.categoryName || "").trim() || undefined,
    startDate: formatOfferStartDate(raw.startDateInMillis),
    expiry: formatOfferEndDate(raw.endDateInMillis),
    expiresAtMillis: endMs,
    cta: "View offer",
    offerUrl: normalizePublicOfferUrl(raw.offerURL),
    termsLines: parseOfferTermsLines(
      raw.termsAndConditions ?? raw.termsandConditions
    ),
    addressDetails: parseOfferAddressDetailsFromItem(raw),
  };
}
